import React, { useEffect, useState } from "react";
import { useFormik, Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import {
  updateAdminAsync,
  createAdminAsync,
} from "../../redux/adminManagementApi";
import { Link, Redirect } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { Col, Row, Modal } from "react-bootstrap";
import { PASSWORD_REGEX } from "../../../../utils/constants";
import axios from "axios";
import { showSuccessSnackbar } from "../../../snackBar/snackBar.action";

const MENU_OPTIONS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "adminManagement", label: "Administrators" },
  {
    key: "services",
    label: "Services",
    subTitles: [
      { key: "services.category", label: "Category Management" },
      { key: "services.subcategory", label: "Subcategory Management" },
    ],
  },
  { key: "cms", label: "CMS Pages" },
  { key: "faq", label: "FAQ Management" },
  { key: "promoCodes", label: "Promo Code Management" },
  { key: "email", label: "Email Management" },
  { key: "virtualToken", label: "Virtual Token Management" },
  { key: "events", label: "Event Management" },
  { key: "challenges", label: "Challenge Management" },
  {
    key: "users",
    label: "User Management",
    subTitles: [
      { key: "users.list", label: "Users" },
      { key: "users.top", label: "Top User Management" },
      { key: "users.referral", label: "Referral Bonus Coins" },
    ],
  },
  { key: "currencies", label: "Currencies" },
  { key: "tokens", label: "Tokens Transactions" },
  { key: "withdrawals", label: "Withdrawal Management" },
  { key: "commission", label: "Commission History" },
  { key: "reports", label: "Reports And Analytics" },
  { key: "tickets", label: "Ticket/Support Management" },
  { key: "team", label: "Team Management" },
  { key: "system", label: "System Settings" },
];

const PERMISSION_CARD_STYLE = {
  backgroundColor: "#0e172b",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  borderRadius: "16px",
  boxShadow: "0 18px 35px rgba(0, 0, 0, 0.45)",
  padding: "20px 24px",
};

const PERMISSION_TITLE_STYLE = {
  fontSize: "15px",
  letterSpacing: "0.04em",
  color: "#f8fafc",
};

const PERMISSION_META_STYLE = {
  fontSize: "11px",
  letterSpacing: "0.12em",
  color: "#94a3b8",
};

const SUB_TITLE_STYLE = {
  fontSize: "13px",
  color: "#e2e8f0",
  fontWeight: 600,
};

const PERMISSION_DESCRIPTION_STYLE = {
  fontSize: "12px",
  color: "#94a3b8",
  marginBottom: 0,
};

const CHECKBOX_STYLE = {
  width: "18px",
  height: "18px",
  borderRadius: "4px",
  border: "1px solid #475569",
  backgroundColor: "transparent",
  accentColor: "#4c82fb",
  cursor: "pointer",
};

const BasicDetailsForm = ({
  selectedUser,
  isLoading,
  reDirect,
  showAdminModal,
  setShowAdminModal,
}) => {
  const dispatch = useDispatch();
  const { authToken } = useSelector((state) => state.auth, shallowEqual);

  const [twoFALoading, setTwoFALoading] = useState(false);
  const deriveTwoFAEnabled = (twoFAData) =>
    Boolean(
      twoFAData?.isEnabled ??
        selectedUser?.requires2FA ??
        selectedUser?.twoFactorAuthentication ??
        false
    );

  const [twoFAEnabled, setTwoFAEnabled] = useState(
    deriveTwoFAEnabled(selectedUser?.twoFactorAuth)
  );

  useEffect(() => {
    const fetchAdminTwoFA = async () => {
      if (!showAdminModal || !selectedUser?._id) return;
      try {
        const { data } = await axios({
          method: "GET",
          url: `${process.env.REACT_APP_HOST}${process.env.REACT_APP_ADMIN_BASE_URL}/getAdminProfile/${selectedUser._id}`,
          headers: authHeaders,
        });
        if (data) {
          const twoFAData =
            data?.data?.user?.twoFactorAuth ??
            data?.data?.twoFactorAuth ??
            data?.twoFactorAuth ??
            null;
          setTwoFAEnabled(deriveTwoFAEnabled(twoFAData));
        }
      } catch (error) {
        // ignore, rely on existing state
      }
    };

    fetchAdminTwoFA();
  }, [showAdminModal, selectedUser?._id]);

  const handleTwoFASetup = () => {
    dispatch(
      showSuccessSnackbar(
        "info",
        "Ask the administrator to enable 2FA from their profile.",
        3000
      )
    );
  };

  const authHeaders = {
    "Content-Type": "application/json",
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  };

  const handleTwoFADisable = async () => {
    if (!selectedUser?._id || !twoFAEnabled) return;
    if (twoFALoading) return;
    try {
      setTwoFALoading(true);
      const { data } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST}${process.env.REACT_APP_ADMIN_BASE_URL}/twoFactor/disable`,
        headers: authHeaders,
        data: { user_id: selectedUser._id },
      });
      if (data?.statusCode === 200) {
        setTwoFAEnabled(false);
        dispatch(
          showSuccessSnackbar("success", "2FA Disabled Successfully", 3000)
        );
      } else {
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Unable to disable 2FA.",
            3000
          )
        );
      }
    } catch (error) {
      dispatch(
        showSuccessSnackbar(
          "error",
          error?.response?.data?.message || "Unable to disable 2FA.",
          3000
        )
      );
    } finally {
      setTwoFALoading(false);
    }
  };

  //formik validation schema
  const BasicDetailsFormSchema = () =>
    Yup.object().shape({
      email: Yup.string()
        .trim()
        .email("Enter valid email")
        .min(3, "Minimum 3 characters")
        .max(50, "Maximum 50 characters")
        .required("Email is required"),
      displayName: Yup.string()
        .trim()
        .min(3, "Minimum 3 characters")
        .max(50, "Maximum 50 characters")
        .required("Name is required"),
      password:
        selectedUser && Object.keys(selectedUser).length === 0
          ? Yup.string()
              .trim()
              .matches(
                PASSWORD_REGEX,
                "Password should contain min 6 and max 32 characters with atleast one Alphanumeric and special character."
              )
              .required("Please enter password")
          : Yup.string(),
      confirmPassword:
        selectedUser && Object.keys(selectedUser).length === 0
          ? Yup.string()
              .trim()
              .oneOf(
                [Yup.ref("password"), null],
                "Password and confirm password didn't match"
              )
              .required("Please enter confirm password")
          : Yup.string(),
      allowedMenus: Yup.array().of(Yup.string())
    });
  //close add admin model after add/edit admin api call
  useEffect(() => {
    if (reDirect === true) {
      setShowAdminModal(false);
    }
  }, [reDirect]);

  const enableTwoFADisabled =
    twoFALoading || !selectedUser?._id || twoFAEnabled;
  const disableTwoFADisabled =
    twoFALoading || !selectedUser?._id || !twoFAEnabled;

  const twoFALabelText = twoFAEnabled ? "Enable 2FA" : "Disable 2FA";
  const twoFALabelClass = twoFAEnabled
    ? "badge-success"
    : "badge-warning text-dark";

  const twoFAButtonStyle = {
    minWidth: 140,
    borderRadius: 9999,
    padding: "0.45rem 1.5rem",
    fontWeight: 600,
    fontSize: "0.9rem",
  };

  return (
    <>
      <Modal
        show={showAdminModal}
        onHide={() => setShowAdminModal(false)}
        centered
        size="xl"
        className="def_modal delete_modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fn_s_18 color_blk f-w-700">
            {selectedUser && selectedUser._id ? "Edit Admin" : "Add Admin"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            enableReinitialize
            initialValues={{
              displayName:
                selectedUser && selectedUser.displayName
                  ? selectedUser.displayName
                  : "",
              email:
                selectedUser && selectedUser.email ? selectedUser.email : "",
              password: "",
              confirmPassword: "",
              allowedMenus:
                selectedUser && Array.isArray(selectedUser.allowedMenus)
                  ? selectedUser.allowedMenus
                  : []
            }}
            validationSchema={BasicDetailsFormSchema}
            onSubmit={(values, { setErrors, resetForm }) => {
              if (!selectedUser._id) {
                return dispatch(createAdminAsync(values));
              }
              if (selectedUser._id) {
                let updatedValues = {};
                updatedValues.displayName = values.displayName;

                if (selectedUser.email !== values.email) {
                  updatedValues.email = values.email;
                }

                if (values.password) {
                  updatedValues.password = values.password;
                }

                if (Array.isArray(values.allowedMenus)) {
                  updatedValues.allowedMenus = values.allowedMenus;
                }

                dispatch(updateAdminAsync(updatedValues, selectedUser._id));
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              isSubmitting,
              handleBlur,
              handleChange,
              setFieldValue,
              setFieldTouched,
              getFieldProps,
            }) => (
              <Row className="justify-content-center">
                <Col lg={12}>
                  <Form autoComplete="off" className="form def_form frmwtpddng">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="form-group">
                          <label className="form-label">Name</label>
                          <input
                            placeholder="Name"
                            type="text"
                            className={`form-control border-less-input`}
                            name="displayName"
                            {...getFieldProps("displayName")}
                          />
                          {touched.displayName && errors.displayName ? (
                            <div className="fv-plugins-message-container">
                              <div className="fv-help-block">
                                {errors.displayName}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="form-group">
                          <label className="form-label">Email</label>
                          <input
                            placeholder="Email"
                            type="email"
                            className={`form-control border-less-input`}
                            name="email"
                            autoComplete="new-email"
                            {...getFieldProps("email")}
                            //disabled={selectedUser.email ? true : false}
                          />
                          {touched.email && errors.email ? (
                            <div className="fv-plugins-message-container">
                              <div className="fv-help-block">
                                {errors.email}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="form-group">
                          <label className="form-label">Password</label>
                          <input
                            placeholder="Password"
                            type="password"
                            className={`form-control  border-less-input`}
                            name="password"
                            autoComplete="new-password"
                            {...getFieldProps("password")}
                            //disabled={selectedUser.password ? true : false}
                          />
                          {touched.password && errors.password ? (
                            <div className="fv-plugins-message-container">
                              <div className="fv-help-block">
                                {errors.password}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="form-group">
                          <label className="form-label">Confirm Password</label>
                          <input
                            placeholder="Confirm Password"
                            type="password"
                            className={`form-control border-less-input`}
                            name="confirmPassword"
                            {...getFieldProps("confirmPassword")}
                            //disabled={selectedUser.password ? true : false}
                          />
                          {touched.confirmPassword && errors.confirmPassword ? (
                            <div className="fv-plugins-message-container">
                              <div className="fv-help-block">
                                {errors.confirmPassword}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="col-md-12">
                        <div className="form-group">
                          <label className="form-label mb-3">Module Permissions</label>
                          <div className="row">
                            {MENU_OPTIONS.map((opt) => {
                              const currentMenus = Array.isArray(values.allowedMenus)
                                ? values.allowedMenus
                                : [];

                              const hasSubTitles = opt.subTitles && opt.subTitles.length > 0;
                              const allSubTitlesSelected = hasSubTitles
                                ? opt.subTitles.every((sub) => currentMenus.includes(sub.key))
                                : false;
                              const isMainTitleChecked = hasSubTitles
                                ? allSubTitlesSelected
                                : currentMenus.includes(opt.key);

                              const handleMainTitleChange = (checked) => {
                                let updatedMenus = [...currentMenus];

                                if (hasSubTitles) {
                                  if (checked) {
                                    opt.subTitles.forEach((sub) => {
                                      if (!updatedMenus.includes(sub.key)) {
                                        updatedMenus.push(sub.key);
                                      }
                                    });
                                  } else {
                                    updatedMenus = updatedMenus.filter(
                                      (key) => !opt.subTitles.some((sub) => sub.key === key)
                                    );
                                  }
                                } else if (checked) {
                                  if (!updatedMenus.includes(opt.key)) {
                                    updatedMenus.push(opt.key);
                                  }
                                } else {
                                  updatedMenus = updatedMenus.filter((k) => k !== opt.key);
                                }

                                setFieldValue("allowedMenus", updatedMenus);
                              };

                              const handleSubTitleChange = (subKey, checked) => {
                                let updatedMenus = [...currentMenus];

                                if (checked) {
                                  if (!updatedMenus.includes(subKey)) {
                                    updatedMenus.push(subKey);
                                  }
                                } else {
                                  updatedMenus = updatedMenus.filter((k) => k !== subKey);
                                }

                                setFieldValue("allowedMenus", updatedMenus);
                              };

                              return (
                                <div key={opt.key} className="col-xl-6 col-lg-12 mb-4">
                                  <div style={PERMISSION_CARD_STYLE}>
                                    <div className="d-flex justify-content-between align-items-start mb-3 pb-2 border-bottom border-secondary">
                                      <div className="form-check m-0">
                                        <label
                                          className="form-check-label text-uppercase mb-0 d-flex align-items-start gap-3"
                                          htmlFor={`menu-${opt.key}`}
                                          style={{ cursor: "pointer" }}
                                        >
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`menu-${opt.key}`}
                                            checked={isMainTitleChecked}
                                            onChange={(e) => handleMainTitleChange(e.target.checked)}
                                            style={CHECKBOX_STYLE}
                                          />
                                          <span>
                                            <span style={PERMISSION_TITLE_STYLE}>{opt.label}</span>
                                            <div style={PERMISSION_META_STYLE}>Permission</div>
                                          </span>
                                        </label>
                                      </div>

                                    </div>

                                    {hasSubTitles ? (
                                      <div className="row">
                                        {opt.subTitles.map((sub) => (
                                          <div key={sub.key} className="col-sm-6 mb-2">
                                            <div className="form-check">
                                              <label
                                                className="form-check-label d-flex align-items-center gap-2 mb-0"
                                                htmlFor={`menu-${sub.key}`}
                                                style={{ cursor: "pointer" }}
                                              >
                                                <input
                                                  className="form-check-input"
                                                  type="checkbox"
                                                  id={`menu-${sub.key}`}
                                                  checked={currentMenus.includes(sub.key)}
                                                  onChange={(e) =>
                                                    handleSubTitleChange(sub.key, e.target.checked)
                                                  }
                                                  style={CHECKBOX_STYLE}
                                                />
                                                <span style={SUB_TITLE_STYLE}>{sub.label}</span>
                                              </label>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <p style={PERMISSION_DESCRIPTION_STYLE}>
                                        Full access to the {opt.label} module.
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    {selectedUser?._id && (
                      <div className="col-md-12">
                        <div className="form-group">
                          {/* <label className="form-label d-flex justify-content-between align-items-center">
                            <span>2FA Google Authenticator</span>
                            <span
                              className={`badge badge-${
                                twoFAEnabled ? "success" : "secondary"
                              }`}
                            >
                              {twoFAEnabled ? "Enabled" : "Disabled"}
                            </span>
                          </label> */}
                          
                          {/* <p className="text-muted mb-3">
                            Force this administrator to complete Google
                            Authenticator setup for an additional security layer.
                          </p> */}
                          <p className="text-muted mb-3">2FA Google Authenticator</p>
                          <label className="form-label d-flex flex-column gap-1">
                            {/* <span>2FA Google Authenticator</span> */}
                            <span
                              className={`badge badge-${
                                twoFAEnabled ? "success" : "secondary"
                              }  px-3 py-2 `}
                              style={{
                                width: "fit-content",
                                display: "inline-block",
                                fontSize: "0.85rem",
                              }}
                            >
                              {twoFAEnabled ? "Enable 2FA" : "Disable 2FA"}
                            </span>
                          </label>
                          {/* <span
                            className={`badge badge-pill px-4 py-3 font-weight-bold text-uppercase mb-3 ${twoFALabelClass}`}
                          >
                            {twoFALabelText}
                          </span> */}
                          <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-3">
                            {twoFAEnabled && (
                            <button
                              type="button"
                              className="btn btn-blue defpddng show-more-btn font-family-base btn_new"
                              style={twoFAButtonStyle}
                              onClick={twoFAEnabled ? handleTwoFADisable : undefined}
                              disabled={
                                twoFALoading ||
                                !selectedUser?._id ||
                                (!twoFAEnabled && !twoFALoading)
                              }
                            >
                              {twoFALoading
                                ? "Please wait..."
                                : twoFAEnabled
                                ? "Disable 2FA"
                                : "Enable 2FA"}
                            </button>
                            )}
                          </div>
                          {/* {!twoFAEnabled && (
                            <small className="text-muted d-block mt-2">
                              Ask the administrator to enable 2FA from their profile.
                            </small>
                          )} */}
                        </div>
                      </div>
                    )}
                    </div>
                    <div className="form-group  mb-0 mt-4">
                      <div className="form-group mr-5 mb-1 mt-2 bg-dark">
                        <button
                          type="submit"
                          className="btn btn-blue spinnerBtn add-avtar-btn"
                          disabled={isLoading}
                        >
                          <span>
                            {selectedUser && selectedUser._id
                              ? "Update"
                              : "Add"}
                          </span>
                          {isLoading && (
                            <div className="ml-3 basic-verification-loader text-center">
                              <CircularProgress />
                            </div>
                          )}
                        </button>
                      </div>
                    </div>
                  </Form>
                </Col>
              </Row>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};
export default BasicDetailsForm;
